import moment from 'moment-timezone';
import { parseISO } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import dayJsTimezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { PDFDocument, StandardFonts } from 'pdf-lib';

import {
  REACT_APP_AUTH_CLIENT_ID,
  REACT_APP_PF_ADMIN_ID,
} from '../constants/envConstants';
import { SESSION_LINKS } from '../components/Analytics/Dashboard/constants/constants';
import {
  clientDetails,
  dateFilters,
  globalDashboardDefaultFilters,
  globalFilters,
  PF_ADMIN,
} from '../constants';
import permissions from '../config/permissions';
import { DATE_TIME_FORMAT_AM_PM } from '../constants/datetime.constant';

import PFDate from './PFDate.helper';

dayjs.extend(utc);
dayjs.extend(dayJsTimezone);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const DEFAULT_TIMEZONE = 'US/Eastern';

export const setMomentTimezone = (timezone = DEFAULT_TIMEZONE) => {
  moment.tz.setDefault(timezone);
  dayjs.tz.setDefault(timezone);
};

export const momentTz = moment;

export const dayjsTz = dayjs.tz;

export const formatDate = date =>
  date
    ? momentTz(parseISO(date?.toString()?.slice(0, 10))).format('MM-DD-YYYY')
    : '';

export const formatDateDefault = (date, divider = '/') => {
  if (typeof date === 'object') {
    const dt = date?.getDate();
    const month = date?.getMonth() + 1;
    const year = date?.getFullYear();
    return `${month}${divider}${dt}${divider}${year}`;
  } else if (typeof date === 'string' && date !== 'Invalid Date') {
    if (date.includes(',')) {
      date = date.split(',')[0];
    }
    return date;
  } else {
    return null;
  }
};

export const formatDateTimeMDY = date => {
  return date ? momentTz(date).format('MM-DD-YYYY') : '';
};
export const formatDateTimeMDYHM = date => {
  return date ? momentTz(date).format('MM-DD-YYYY hh:mm:ss A') : '';
};
export const formatDateTime24HrMDYH = date => {
  return date ? momentTz(date).utc().format('MM-DD-YYYY HH:mm:ss') : '';
};
export const formatTimeHM = date => {
  return date ? momentTz(date).format('hh:mm A') : '';
};
export const formatDateTime = date => {
  return date ? momentTz(date).format('MM-DD-YYYY hh:mm A') : '';
};
export const dayjsFormatDateTimeMDY = date => {
  return date ? dayjsTz(date).format('MM-DD-YYYY') : '';
};

export const dayjsFormatDateTime = date => {
  return date ? dayjsTz(date).format('MM-DD-YYYY hh:mm A') : '';
};
export const toFiniteNumberOr = (value, fallback = 0) => {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

export const dayjsConvertUTCStringToClientTimeZoneDayJSObj = utcString => {
  return utcString ? dayjs.utc(utcString).tz() : null;
};

// Merge a date object and a time object into a single DateTime
export const mergeDateAndTime = (dateObj, timeObj) => {
  return dayjs(dateObj)
    .hour(dayjs(timeObj).hour())
    .minute(dayjs(timeObj).minute())
    .second(0);
};

// merge a Date object with a time string "HH:mm:ss"
export const mergeDateObjAndTimeString = (dateObj, timeStr) => {
  const [hour, minute] = timeStr.split(':').map(Number);
  return dayjs(dateObj).hour(hour).minute(minute).second(0);
};

export const formatDayJSObj = (date, format = DATE_TIME_FORMAT_AM_PM) => {
  return date ? date.format(format) : '';
};

export const convertLocalDateToDayJsUTCObj = localDateObj => {
  return dayjs(localDateObj).utc();
};

export const convertUTCArrayToIST = utcDates => {
  if (Array.isArray(utcDates)) {
    return utcDates.map(utcDate => {
      const utcReceivedDate = utcDate ? utcDate : utcDates[0];
      const date = new Date(utcReceivedDate);
      date.setHours(date.getHours() + 5);
      date.setMinutes(date.getMinutes() + 30);
      return date.toISOString();
    });
  }
  return utcDates;
};

export const checkPermission = (
  value,
  pageTitle = null,
  permissionList = []
) => {
  const clientId = localStorage.getItem('client_id');
  const tokenPermissions = permissionList.length
    ? permissionList
    : JSON.parse(localStorage.getItem('permissions'));
  if (tokenPermissions?.includes(value)) {
    const localAuthClient = REACT_APP_AUTH_CLIENT_ID === 'test';
    if (pageTitle === 'Tenant Billing') {
      if (localAuthClient) {
        return clientId === PF_ADMIN ? true : false;
      } else {
        return clientId === REACT_APP_PF_ADMIN_ID ? true : false;
      }
    }
    return true;
  } else {
    return false;
  }
};

export const getUrlExtension = url => {
  return url.split(/[#?]/)[0].split('.').pop().trim();
};

export const downloadFile = (
  docDownloadUrl,
  documentName,
  setDownloadDocumentLoading
) => {
  if (docDownloadUrl && documentName) {
    setDownloadDocumentLoading(true);
    fetch(docDownloadUrl).then(response => {
      response.blob().then(blob => {
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = documentName;
        a.click();
        setDownloadDocumentLoading(false);
      });
    });
  }
};

export const checkDollarSign = str => {
  var pattern = new RegExp('[$]');
  if (!pattern.test(str)) return false;
  const d = String(str);
  return d === str; // valid string
};

export const accessKeyFromObject = (obj, key) => {
  if (key.includes('.')) {
    let newValue = obj;
    key.split('.').forEach(element => {
      if (newValue) {
        newValue = newValue[element];
      }
    });
    return newValue;
  }
  return obj[key];
};

export const tableValueFormatter = (headers, val) => {
  let value = headers.value_accessor
    ? accessKeyFromObject(val, headers.value_accessor)
    : '';
  if (headers.prefix && accessKeyFromObject(val, headers.value_accessor)) {
    value = `${headers.prefix} ${accessKeyFromObject(val, headers.value_accessor)}`;
  }
  if (
    headers.type === 'date' &&
    accessKeyFromObject(val, headers.value_accessor)
  ) {
    value = formatDate(val[headers.value_accessor]);
  }
  if (
    headers.fixToDecimal &&
    accessKeyFromObject(val, headers.value_accessor)
  ) {
    value = parseFloat(
      accessKeyFromObject(val, headers.value_accessor)
    )?.toFixed(parseInt(headers.fixToDecimal));
  }
  if (
    headers.fixToDecimal &&
    headers.prefix &&
    accessKeyFromObject(val, headers.value_accessor)
  ) {
    value = `${headers.prefix} ${parseFloat(
      accessKeyFromObject(val, headers.value_accessor)
    )?.toFixed(parseInt(headers.fixToDecimal))}`;
  }
  return value;
};

// For displaying time in tables
export const convert24HourTimeTo12 = timeString => {
  if (timeString) {
    let [hours, minutes] = timeString?.split(':') || [];
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return hours + ':' + minutes + ' ' + meridiem;
  }
  return null;
};

// For setting timePicker values from API response
export const convertTimeForTimePickerValues = timeString => {
  if (timeString) {
    const [hours, minutes] = timeString?.split(':') || [];
    return hours + ':' + minutes;
  }
  return null;
};

// For setting datetime-locale format value from date string
export const convertDateToLocaleString = (
  dateString,
  format = 'YYYY-MM-DDTHH:mm'
) => {
  return momentTz(dateString).format(format);
};

export const getBulletinMessage = bulletinObj => {
  if (bulletinObj?.is_important && bulletinObj?.url) {
    return (
      <>
        <span className="text-color">{bulletinObj.message}</span>
        <a
          href={bulletinObj.url}
          target="_blank"
          className="text-xs ml-2 text-primary"
          rel="noreferrer"
        >
          Read more
        </a>
      </>
    );
  }
  if (bulletinObj.is_important) {
    return <b>{bulletinObj.message}</b>;
  }
  if (bulletinObj.url) {
    return (
      <>
        <span className="text-color">{bulletinObj.message}</span>
        <a
          href={bulletinObj.url}
          target="_blank"
          className="text-xs ml-2 text-primary"
          rel="noreferrer"
        >
          Read more
        </a>
      </>
    );
  }
  return bulletinObj.message;
};

export const getLoginUserId = () => localStorage.getItem('user_id');

export const getCurrentClientId = () => localStorage.getItem('client_id');

export const toTitleCase = inputString => {
  if (!inputString) return '';
  const words = inputString?.split(' ');
  const titleCaseWords = words?.map(word => {
    if (word?.length === 1) {
      return word?.toUpperCase();
    }
    return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
  });
  const titleCaseString = titleCaseWords?.join(' ');
  return titleCaseString;
};

export const isValidHttpUrl = string => {
  let url;

  try {
    url = new URL(string);
  } catch (error) {
    console.error(error);
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

/**
 * Checks if passed url is valid.
 * No console log on error
 * @param {url} string
 * @returns
 */
export const isValidHttpUrlV2 = string => {
  let url;

  try {
    url = new URL(string);
  } catch {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

const findCategoryType = (addressComponents, type) => {
  return addressComponents.find(address => address.types.includes(type));
};

export const transformAddressFromGeocode = addressComponents => {
  const address = {
    addressLineOne: findCategoryType(addressComponents, 'street_number')
      ?.long_name,
    addressLineTwo: findCategoryType(addressComponents, 'route')?.long_name,
    city: findCategoryType(addressComponents, 'locality')?.long_name,
    state: findCategoryType(addressComponents, 'administrative_area_level_1')
      ?.long_name,
    zipcode: findCategoryType(addressComponents, 'postal_code')?.long_name,
  };

  return address;
};

export const isRoEnabled = () => {
  if (clientDetails?.routing_module) {
    return true;
  }
  return false;
};

export const hasSpecialCharacters = inputString => {
  // eslint-disable-next-line no-useless-escape
  const specialCharacterPattern = /[!@#$%^&*()_+{}\[\]:;<>,.?~\\|\-=]/;
  return specialCharacterPattern.test(inputString);
};

export const containsNumbers = inputString => {
  const regex = /\d/;
  return regex.test(inputString);
};

export const getInitials = name => {
  if (!name) return '';
  const rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
  const initials = [...name.matchAll(rgx)] || [];
  return (
    (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
  )?.toUpperCase();
};

export const isValidObject = data =>
  !Array.isArray(data) &&
  data instanceof Object &&
  data.constructor.name == 'Object';

const handleQueryParamValue = value => {
  if (Array.isArray(value)) return value.join(',');
  return value;
};
export const convertToQueryParams = params => {
  let queryParams = '',
    isFirst = true;
  if (isValidObject(params)) {
    for (const key in params) {
      if (isFirst) {
        queryParams += `?${key}=${handleQueryParamValue(params[key])}`;
        isFirst = false;
      } else {
        queryParams += `&${key}=${handleQueryParamValue(params[key])}`;
      }
    }
  }
  return queryParams;
};
export const isCrmEnabled = () => {
  if (clientDetails?.crm_module) {
    return true;
  }
  return false;
};

export const isClientPFINC = () => {
  const clientId = localStorage.getItem('client_id');
  return clientId === PF_ADMIN;
};

export const isWarrantyEnabled = () => {
  try {
    if (clientDetails && clientDetails.warranty_module) {
      return true;
    }
  } catch (error) {
    console.log(error);
    return false;
  }
  return false;
};

export const convertHexToRgb = hexColor => {
  // remove # symbol from the hex color code
  let hex = hexColor.replace('#', '');
  // convert the hex color to RGB color
  let red = parseInt(hex.substring(0, 2), 16);
  let green = parseInt(hex.substring(2, 4), 16);
  let blue = parseInt(hex.substring(4, 6), 16);
  // set the RGB color value
  return [red, green, blue];
};

// Will get optimal text color based on the background color
export const getOptimalTextColor = hexColor => {
  const [red, green, blue] = convertHexToRgb(hexColor);

  const [rs, gs, bs] = [red, green, blue].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  const luminance = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export const checkIfNull = value => {
  return value === null;
};

export const capitalizeFirstLetter = string => {
  if (!string) return null;
  const lowerString = string.toLowerCase();
  return lowerString.charAt(0).toUpperCase() + lowerString.slice(1);
};

export const capitalizeFirstLetterOfEachWord = (string, splitStr = ' ') => {
  if (!string) return null;
  return string
    .split(splitStr)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getUserTimeZone = () => {
  const localStorageTimeZone = localStorage.getItem('client_timezone');
  return localStorageTimeZone?.length > 0
    ? localStorageTimeZone
    : moment.tz.guess();
};

export const getConvertedDate = (timeZone, inputDate) => {
  if (!inputDate) return null;
  const resultDate = timeZone
    ? moment(inputDate).tz(timeZone)
    : moment(inputDate);
  return resultDate.format('MM-DD-YYYY');
};

export const calculateAllocationPercentage = (laborItems, projectItemId) => {
  try {
    let sum = 0;
    for (
      let projectIndex = 0;
      projectIndex < laborItems.length;
      projectIndex++
    ) {
      const pi = laborItems[projectIndex];
      if (pi.project_item_id === projectItemId) {
        sum += pi.project_item_user?.percentage
          ? pi.project_item_user?.percentage
          : 0;
      }
    }
    return sum;
  } catch (error) {
    console.log(error);
    return 0;
  }
};

export const check100PercentAllocation = laborItems => {
  const laborSet = new Set();
  let isPaidStatus = false;
  for (let i = 0; i < laborItems?.length; i++) {
    const li = laborItems[i];
    if (li?.paid_status) {
      isPaidStatus = true;
    }
    if (!laborSet.has(li.project_item_id)) {
      laborSet.add(li.project_item_id);
      const allocationPercentage = calculateAllocationPercentage(
        laborItems,
        li.project_item_id
      );
      if (allocationPercentage !== 100) {
        return { allocationPercentage: false, isPaidStatus: isPaidStatus };
      }
    }
  }
  return { allocationPercentage: true, isPaidStatus: isPaidStatus };
};

export const formatCurrency = input => {
  // Parse the input as a number
  const number = parseFloat(input);

  // Check if the input is a valid number
  if (isNaN(number)) {
    return 'Invalid input';
  }

  // Format the number as a currency string with commas
  const formattedCurrency = number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD', // You can change the currency code as needed
  });

  return formattedCurrency;
};

export const formatNumber = (
  value,
  decimalPlaces = 2,
  forceDecimal = false
) => {
  const parsedValue = parseFloat(value);
  if (!isNaN(parsedValue)) {
    if (Number.isInteger(parsedValue)) {
      if (forceDecimal) return `${parseInt(value)}.00`;
      else return parseInt(value);
    }

    const decimalStr = parsedValue.toString();
    const parts = decimalStr.split('.');
    if (parts.length < 2) {
      return value;
    }

    if (parts[1].length === 1) parts[1] = `${parts[1]}0`;
    if (parts[1].length === 0) parts[1] = `00`;

    const decimalPart = `${parts[0]}.${parts[1].slice(0, decimalPlaces)}`;
    return decimalPart;
  }
  return null;
};

const parseDecimalMinutes = decimalMinutes => {
  if (!decimalMinutes || isNaN(decimalMinutes)) return null;

  const totalSeconds = Math.round(parseFloat(decimalMinutes) * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return { mins, secs };
};

export const formatMinutesToReadableTime = minutes => {
  const parsed = parseDecimalMinutes(minutes);
  if (!parsed) return '';

  const { mins, secs } = parsed;
  return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
};

// Convert decimal minutes to MM:SS format for display in input in labor items service time
export const convertDecimalMinutesToTimeFormat = decimalMinutes => {
  const parsed = parseDecimalMinutes(decimalMinutes);
  if (!parsed) return '';

  const { mins, secs } = parsed;
  if (secs === 0) {
    return mins.toString();
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Convert flexible time input to decimal minutes for API
// Handles: 22, 22:53, 0:40, 3, 123:45, 123
export const convertTimeFormatToDecimalMinutes = timeString => {
  if (!timeString || timeString.trim() === '') return null;

  // Remove any mask placeholders and trim
  const cleanInput = timeString.replace(/_/g, '').trim();
  if (!cleanInput) return null;

  if (cleanInput.includes(':')) {
    const [minutesPart, secondsPart] = cleanInput.split(':');
    const minutes = parseInt(minutesPart) || 0;
    const seconds = parseInt(secondsPart) || 0;

    if (seconds >= 60) return null;

    const result = minutes + seconds / 60;
    return Math.round(result * 10000) / 10000;
  } else {
    // Just minutes (22, 3, 123)
    const minutes = parseInt(cleanInput);
    if (isNaN(minutes)) return null;

    return minutes;
  }
};

export const toHelperInput = v => (v === 0 ? '0' : v);

export const openNewWindow = url => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const redirectPage = (url, redirectionMethod = '_blank') => {
  if (redirectionMethod === '_self') {
    window.location.replace(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

//Function to validate email
export const isEmail = email => {
  const regex =
    // eslint-disable-next-line no-useless-escape
    /^([a-zA-Z0-9_\.\-\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  if (!regex.test(email)) {
    return false;
  } else {
    return true;
  }
};

export const floatParser = value => {
  if (!isNaN(value)) {
    const no = parseFloat(value);
    return no > 0 ? no.toFixed(2) : '0.00';
  } else {
    return '0.00';
  }
};

// Function to check given value in time format
export const isValidTimeFormat = inputString => {
  // Regular expression to match the format HH:MM:SS
  const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
  return timeRegex.test(inputString);
};

export const onlyLettersAndNumbers = str => {
  const regexPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-11ee-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return regexPattern.test(str);
};
export const isMeasureSquareEnabled = (isRequired = 0) => {
  let res = false;
  if (clientDetails?.is_measuresquare && Boolean(parseInt(isRequired))) {
    res = true;
  }

  return res;
};

export const lockGlobalBar = () => {
  let res = false;
  if (clientDetails?.fixed_top_bar && clientDetails?.fixed_top_bar) {
    res = true;
  }
  return res;
};

export const shippingAndReceivingModule = () => {
  let res = false;
  if (clientDetails?.shipping_receiving && clientDetails?.shipping_receiving) {
    res = true;
  }
  return res;
};

export const lockProjectBar = () => {
  let res = false;
  if (clientDetails?.fixed_project_bar && clientDetails?.fixed_project_bar) {
    res = true;
  }
  return res;
};

export const addDays = (date, days) => {
  date.setDate(date.getDate() + days);
  return date;
};

// Helper to format date using dayjs
export const dateFormatter = (value, dateFormat) => {
  return dayjs(value).format(dateFormat);
};

export const createDateFromTimeString = timeString => {
  if (timeString.includes('undefined')) {
    return null;
  }
  const [hours, minutes] = timeString?.split(':')?.map(Number) || [];
  const currentDate = new Date();
  currentDate?.setHours(hours);
  currentDate?.setMinutes(minutes);
  currentDate?.setSeconds(0);
  return currentDate;
};

export const getTimeFromDate = date => {
  const hours = String(date?.getHours())?.padStart(2, '0');
  const minutes = String(date?.getMinutes())?.padStart(2, '0');
  const formattedTime = `${hours}:${minutes}`;
  return formattedTime;
};

export const convertTo12HourFormat = time24Hour => {
  const [hours, minutes] = time24Hour.split(':').map(Number);
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  let hours12Hour = hours % 12;
  hours12Hour = hours12Hour === 0 ? 12 : hours12Hour;
  const formattedTime = `${hours12Hour}:${String(minutes).padStart(2, '0')} ${meridiem}`;
  return formattedTime;
};

export const convertToMinutes = time => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const timeDifference = (time1, time2) => {
  const minutes1 = convertToMinutes(time1);
  const minutes2 = convertToMinutes(time2);
  const differenceInMinutes = Math.abs(minutes1 - minutes2);
  const hoursDifference = Math.floor(differenceInMinutes / 60);
  const minutesDifference = differenceInMinutes % 60;
  const formattedDifference =
    (hoursDifference < 10 ? '0' : '') +
    hoursDifference +
    ':' +
    (minutesDifference < 10 ? '0' : '') +
    minutesDifference;
  return formattedDifference;
};

export const isStartTimeLessThanEndTime = (start, end) => {
  const [startHours, startMinutes] = start.split(':').map(Number);
  const [endHours, endMinutes] = end.split(':').map(Number);
  if (
    startHours < endHours ||
    (startHours === endHours && startMinutes < endMinutes)
  ) {
    return true;
  }
  return false;
};

// ceil time to nearest minute
export const ceilToMinute = dateValue => {
  if (!dateValue) return null;

  const date = new Date(dateValue);

  if (isNaN(date.getTime())) return null;

  const seconds = date.getSeconds();
  const milliseconds = date.getMilliseconds();

  if (seconds > 0 || milliseconds > 0) {
    date.setMinutes(date.getMinutes() + 1);
    date.setSeconds(0, 0);
  } else {
    date.setSeconds(0, 0);
  }

  return date;
};

export const updateArrayWithMatchingDays = (firstArray, secondArray) => {
  firstArray.forEach((item, index) => {
    const matchingItem = secondArray.find(
      secondItem => secondItem.day === item.day
    );
    if (matchingItem) {
      firstArray[index] = { ...matchingItem };
    }
  });
  return firstArray;
};
// Helper to check future date
export const checkFutureDate = value => {
  return dayjs(value).isAfter(dayjs());
};

export const findTechnician = value => {
  const stringValue = JSON?.stringify(value);
  return /\btechnician\b/.test(stringValue);
};

export const checkDefaultPo = value => {
  return value?.includes('default-po');
};

export const showFirst30Characters = value => {
  if (value?.length > 15) {
    return value?.substring(0, 15) + '...';
  }
  return value;
};

export const checkUrlString = (value, source) => {
  return value?.includes(source);
};
export const disabledLoadMore = totalCount => {
  return Math.ceil(totalCount / 10);
};

export const ellipsisString = (value, maxValue) => {
  if (value?.length > maxValue) {
    return value?.substring(0, maxValue) + '...';
  }
  return value;
};

export const ellipsisStringNoCondition = (value, maxValue) => {
  return value?.substring(0, maxValue) + '...';
};

export const getPercentageValue = value => {
  const percentageValue = value > 100 ? 0 : Math.max(0, value);
  return percentageValue;
};

export const getInitialsPR = name => {
  if (!name) return '';
  let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
  let initials = [...name.matchAll(rgx)] || [];
  initials = (
    (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
  ).toUpperCase();
  return initials;
};

export const doesKeyExistInArray = (value, key) => {
  if (value) {
    const isKeyPresent = value?.some(item =>
      Object.prototype.hasOwnProperty.call(item, key)
    );
    if (isKeyPresent) {
      return true;
    } else {
      return false;
    }
  } else {
    return true;
  }
};

export const removeLinkSession = () => {
  sessionStorage.removeItem(SESSION_LINKS.VIEW_LINKS);
  sessionStorage.removeItem(SESSION_LINKS.EDIT_LINKS);
};

export const formatPhoneNumber = phone => {
  return phone ? phone.replace(/[^0-9]/g, '') : '';
};

export const formatNagativeCurrency = value => {
  const num = parseFloat(value);
  if (num < 0) {
    return `-$${Math.abs(num).toFixed(2)}`;
  } else {
    return `$${num.toFixed(2)}`;
  }
};

export const prepareDashboardFilters = values => {
  const filterMap = new Map();
  const dateFilterMap = new Map();
  // Handle filters
  const filterArray = globalFilters;
  const defaultFilterArray = globalDashboardDefaultFilters;

  if (Object.keys(values?.defaultFilter)?.length) {
    defaultFilterArray?.map(filter => {
      if (
        Array.isArray(values?.defaultFilter?.[filter?.value]) &&
        values?.defaultFilter?.[filter?.value]?.length > 0
      ) {
        filterMap.set(filter?.key, {
          member: filter?.key,
          values: values?.defaultFilter?.[filter?.value]?.map(item =>
            item?.toString()
          ),
          operator: 'equals',
        });
      }
    });
  } else {
    filterArray?.map(filter => {
      if (
        Array.isArray(values?.[filter?.value?.[0]]) &&
        values?.[filter?.value?.[0]]?.length > 0
      ) {
        const list = values[filter.value[0]];
        const mappedValues =
          typeof list[0] === 'object'
            ? list?.map(item => item?.[filter?.value?.[1]]?.toString())
            : list?.map(item => item.toString());

        filterMap.set(filter?.key, {
          member: filter?.key,
          values: mappedValues,
          operator: 'equals',
        });
      }
    });
  }

  // Handle Date Filters
  const dateFiltersArray = dateFilters;
  dateFiltersArray?.map(item => {
    if (
      values[item?.value?.[0]]?.length > 0 ||
      values[item?.value?.[1]].length > 0
    ) {
      const startDate =
        values[item?.value?.[0]]?.length > 0
          ? dateFormatter(values[item?.value?.[0]], 'YYYY-MM-DD')
          : null;
      const endDate = dateFormatter(values[item?.value?.[1]], 'YYYY-MM-DD');

      const newCondition = {
        dimension: item?.key,
        dateRange: [startDate, endDate],
      };
      dateFilterMap.set(item?.key, newCondition);
    }
  });

  let appliedFilters = Array.from(filterMap.values());
  const technicianFilter = filterMap.get('InstallerDetail.installer_id');
  const companyFilter = filterMap.get('InstallerDetail.installer_company_id');
  if (technicianFilter && companyFilter) {
    appliedFilters = appliedFilters
      .filter(filter => filter !== technicianFilter && filter !== companyFilter)
      .concat({ or: [technicianFilter, companyFilter] });
  }
  const appliedDateFilters = Array.from(dateFilterMap.values());

  return [appliedFilters, appliedDateFilters];
};

export const splitAndGetValues = value => {
  const splitValues = typeof value === 'string' ? value.split(' / ') : [];

  const newValue = splitValues?.map(value => ({
    id: value,
    name: value,
    is_split: 1,
  }));
  return newValue;
};

export const matchExactString = (url, value) => {
  const urlObj = new URL(url);
  return urlObj?.pathname === value;
};

export const storesWithFormattedNames = storeList => {
  return storeList.map(store => ({
    ...store,
    store_name: `${store.store_number} - ${store.store_name}`,
  }));
};

export const checkAllNotesPermissions = clientDefaultNotesType => {
  switch (clientDefaultNotesType) {
    case 'Delivery':
      return checkPermission(permissions?.viewEditProject?.addDeliveryNotes);

    case 'Installer':
      return checkPermission(permissions?.viewEditProject?.addInstallerNotes);

    case 'Internal':
      return checkPermission(permissions?.viewEditProject?.addInternalNotes);

    case 'Retailer':
      return checkPermission(permissions?.viewEditProject?.addRetailerNotes);

    case 'Pinned':
      return checkPermission(permissions?.viewEditProject?.addScheduledNotes);

    case 'Important Scheduler Note':
      return checkPermission(
        permissions?.viewEditProject?.addImportantSchedulerNote
      );

    case 'Important Installer Note':
      return checkPermission(
        permissions?.viewEditProject?.addImportantInstallerNote
      );
  }
};
export const AnnotationRegex = /~(.*?)~/;

export const matchWithRegex = (regexPattern, inputString) => {
  if (!inputString || inputString.length <= 0) return null;

  const regexMatch = regexPattern.exec(inputString);
  if (regexMatch && regexMatch[1]) {
    return regexMatch[1];
  }

  return null;
};

export const isValidDateAndConvert = input => {
  try {
    const parsedDate = moment(input, moment.ISO_8601, true);

    if (parsedDate.isValid()) {
      return parsedDate.format('MM/DD/YYYY');
    } else {
      return '';
    }
  } catch {
    return '';
  }
};

export const fillCustomValue = (_value, lastSignedDate) => {
  let valToReturn = '';
  const val = _value;
  if (val?.length > 0) {
    const valArr = val.split(':');
    switch (valArr[0]?.toString()?.trim()?.toLowerCase()) {
      case 'custom date':
        switch (valArr[1]?.toString()?.trim()?.toLowerCase()) {
          case 'yesterday':
            valToReturn = moment().subtract(1, 'days').format('MM-DD-YYYY');
            break;
          case 'today':
            valToReturn = moment().format('MM-DD-YYYY');
            break;

          case 'tommorow':
            valToReturn = moment().add(1, 'days').format('MM-DD-YYYY');
            break;
          case 'lastsigneddate':
            if (lastSignedDate) {
              valToReturn = moment(lastSignedDate)
                .local()
                .format('MM-DD-YYYY hh:mm A');
            }
            break;
          default:
            break;
        }
        break;

      default:
        break;
    }
    return valToReturn;
  } else return '';
};

export const enableReadOnly = _field => {
  const field = _field;
  if (field) {
    field.enableReadOnly();
  }
};

export const getPDFAnnotations = async url => {
  const annotations = [];
  const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  const form = pdfDoc.getForm();
  let fields = null;
  fields = form.getFields();
  fields?.forEach(field => {
    const fieldName = field.getName();
    if (matchWithRegex(AnnotationRegex, fieldName)) {
      annotations.push(fieldName.match(/~.*?~/)[0]);
    }
  });
  return annotations;
};
export const createPdfForPreview = async url => {
  const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  await pdfDoc.embedFont(StandardFonts.TimesRoman);
  await pdfDoc.embedFont(StandardFonts.Helvetica);

  const pdfBytes = await pdfDoc.save({ updateFieldAppearances: true });
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
};

export const toBoolean = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(false);
};
export const DATE_FORMATS = {
  DEFAULT: 'MM-dd-yyyy hh:mm a',
  SERVER_DATE_TIME: 'yyyy-MM-dd HH:mm:ss',
  WITH_SLASH_FORMATE: 'MM/dd/yyyy, hh:mm a',
  MDY_FORMATE: 'MM-dd-yyyy',
  MDY_WITH_SLASH_FORMATE: 'MM/dd/yyyy',
  FULL_DATE_TIME: 'MM-dd-yyyy hh:mm:ss a',
};
export const formatPFDateTimeMDYHM = date => {
  return date ? PFDate(date).format('MM-dd-yyyy hh:mm:ss a') : '';
};

export const formatPFDateString = date => {
  return date ? PFDate(date).format('MM-dd-yyyy') : '';
};
export function checkTimePassed(key, thresholdSeconds) {
  const savedValue = localStorage.getItem(key);
  if (!savedValue) return { passed: false, secondsPassed: null };

  const saved = new Date(savedValue);
  if (isNaN(saved)) return { passed: false, secondsPassed: null };

  const now = new Date();
  const secondsPassed = Math.floor((now - saved) / 1000);
  const passed = secondsPassed >= thresholdSeconds;

  return { passed, secondsPassed };
}

export const clearCookies = () => {
  document?.cookie?.split(';')?.forEach(cookie => {
    const name = cookie?.split('=')[0]?.trim();
    document.cookie = `${name}=; Max-Age=0; path=/`;
  });
};

export const setAccessTokenStorage = (token, expiry) => {
  if (!token) return;

  localStorage.setItem('access_token', token);
  localStorage.setItem('accessTokenExpiryTime', expiry);

  document.cookie = `access_token=${token}; expires=${expiry}; path=/`;
};
export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
export const formatDocumentName = document_name => {
  if (document_name) {
    return (document_name || '')
      .replace(/\.[^./\\]+$/, '')
      .replace(/[_-]\d{4}[_-]\d{2}[-_]\d{2}(?:[_-]\d{2}){3}$/, '')
      .replace(/[_-]\d{4}(?:[_-]\d{2}){5}$/, '');
  } else {
    return '';
  }
};

export const INSTALLER_ZIP_INELIGIBLE_BODY =
  'The technician assigned to this job does not meet the zip code eligibility requirements.';

export const INSTALLER_CATEGORY_SKILL_INELIGIBLE_BODY =
  'The technician assigned to this job does not have the required skill category or skill level.';

export const INSTALLER_ZIP_AND_CATEGORY_SKILL_INELIGIBLE_BODY =
  'The technician assigned to this job is not eligible for the zip code, skill category and skill level.';

export function getInstallerEligibilityBannerText(zipFail, skillFail) {
  if (zipFail && skillFail) {
    return INSTALLER_ZIP_AND_CATEGORY_SKILL_INELIGIBLE_BODY;
  }
  if (zipFail) {
    return INSTALLER_ZIP_INELIGIBLE_BODY;
  }
  if (skillFail) {
    return INSTALLER_CATEGORY_SKILL_INELIGIBLE_BODY;
  }
  return '';
}
export const capitalizeWords = value => {
  if (!value) return '-';
  return value.replace(/\b\w/g, char => char.toUpperCase());
};

export const toLocked = value => Number(value) === 1;
